// src/pages/ReferralLanding.jsx
// Smart landing page para links de academia.
// Detecta plataforma → tenta abrir o app → redireciona para loja se não instalado.
// No iOS, após instalar, o sistema entrega o link original ao app (deferred deep link nativo).

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import LESVITA_LOGO from "../Assets/images/logo/iconeApp1024.webp";
import { supabase } from "../supabaseClient";
import Style from "../styles/landing/ReferralLanding.module.css";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Modal } from "react-bootstrap";
import APP_STORE_LOGO from "../Assets/icons/app_store_logo.svg";
import PLAY_STORE_LOGO from "../Assets/icons/play_store_logo.svg";
import BoxShadow from "../components/layout/BoxShadow.jsx";
import {
  salvarConvitePendente, limparConvitePendente, resolverConvite,
} from "../utils/teamJoinStorage";
import CriadorLink from "./CriadorLink";
import { buscarCriador } from "../features/criador/cupomCriador";

// ─── Configuração das lojas ────────────────────────────────────────────────────
const APP_STORE_ID    = "6753772111"; // ex: 6478291234
const APP_STORE_URL   = `https://apps.apple.com/br/app/lesvita/id${APP_STORE_ID}`;
const PLAY_STORE_URL  = `https://play.google.com/store/apps/details?id=com.lesvita.app`;

// ─── Detecção de plataforma ────────────────────────────────────────────────────
const ua        = navigator.userAgent || "";
const IS_IOS    = /iPhone|iPad|iPod/i.test(ua);
const IS_ANDROID = /Android/i.test(ua);
const IS_MOBILE  = IS_IOS || IS_ANDROID;

/**
 * Extrai um código de afiliado do segmento de caminho, ou "" se não parecer um.
 *
 * Aceita "TADEU05" e "ref=TADEU05". O segundo formato existe porque o "?" do
 * link some com frequência no mundo real — autolink de WhatsApp/Instagram,
 * gente digitando à mão, encurtador mal configurado. Sem isto, cada uma dessas
 * perdas vira uma indicação que nunca existiu.
 */
const codigoDeAfiliado = (segmento) => {
  const bruto = String(segmento || "").replace(/^(ref|cupom)=/i, "");
  const codigo = bruto.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 24);
  return codigo.length >= 3 ? codigo : "";
};

/**
 * Dados públicos da academia para a landing: nome, cidade, logo, cores e se ela
 * aceita vínculo agora.
 *
 * Fonte principal: RPC `get_gym_public_branding`, que devolve só campos visuais.
 * A leitura anônima direta de `gym_partner_profiles` perdeu `partner_user` e as
 * colunas de plano/geo (B2B V2, Bloco A). O fallback para as consultas antigas
 * existe só para a janela em que esta versão da web já está no ar e a migration
 * da RPC ainda não; `error` fica preenchido apenas se as duas vias falharem.
 */
async function carregarAcademiaPublica(referralCode) {
  const rpc = await supabase.rpc("get_gym_public_branding", { p_referral_code: referralCode });
  if (!rpc.error) {
    const b = rpc.data;
    if (!b) return { data: null, themeData: null, error: null };
    const data = {
      gym_name: b.gym_name, city: b.city, state_uf: b.state_uf, logo_url: b.logo_url,
      partner_user: b.accepting_links === true,
    };
    return { data, themeData: { ...b, partner_user: b.accepting_links === true }, error: null };
  }

  const { data, error } = await supabase
    .from("gym_dashboard_view")
    .select("gym_name, city, state_uf, logo_url, partner_user")
    .eq("referral_code", referralCode)
    .maybeSingle();
  if (error || !data) return { data, themeData: null, error };

  const { data: themeData } = await supabase
    .from("gym_partner_profiles")
    .select("partner_user, brand_primary_color, brand_accent_color, button_primary_bg, button_primary_text, dark_primary_color, dark_accent_color, dark_navbar_bg_color")
    .eq("referral_code", referralCode)
    .maybeSingle();
  return { data, themeData, error: null };
}

export default function ReferralLanding() {
  const { referralCode }    = useParams();
  const [searchParams]      = useSearchParams();
  const joinToken           = searchParams.get("join_token") || "";
  // JOIN_CODE da equipe (link LEGADO /ref/{gym}?team={code}). Aditivo.
  const teamCode            = searchParams.get("team") || "";
  // Link NOVO: endereça o documento exato da equipe, sem depender de JOIN_CODE
  // (que só é único DENTRO de uma academia) nem da resolução por referral.
  const competitionId       = searchParams.get("comp") || "";
  const teamId              = searchParams.get("team_id") || "";

  const navigate            = useNavigate();
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tryingApp, setTryingApp] = useState(false);
  // Token de vínculo mintado para o link "pelado" de convite (sem join_token).
  // Todos os trilhos nativos (abrir app, clipboard iOS, install referrer Android)
  // exigem join_token — sem isto o vínculo de academia nunca acontece pelo app.
  const [mintedToken, setMintedToken] = useState("");
  const timerRef            = useRef(null);

  // Código de Criador Parceiro (não é academia): renderiza a página do criador.
  const [criador, setCriador] = useState(null);

  const [showGymReferralModal, setShowGymReferralModal] = useState(false);
  const [gymReferralAction, setGymReferralAction]       = useState(null);
  const [currentGym, setCurrentGym]                     = useState(null);
  const [newGym, setNewGym]                             = useState(null);
  const [isApplyingGymReferral, setIsApplyingGymReferral] = useState(false);

  const auth      = getAuth();
  const functions = getFunctions(undefined, "southamerica-east1");
  const getGymReferralStatus   = httpsCallable(functions, "getGymReferralStatus");
  const applyGymReferralToUser = httpsCallable(functions, "applyGymReferralToUser");

  // ── Carrega dados e cores da academia ────────────────────────────────────────
  useEffect(() => {
    if (!referralCode) return;

    (async () => {
      const { data, themeData, error } = await carregarAcademiaPublica(referralCode);

      // ── Não é link de academia ────────────────────────────────────────────
      // Esta rota é o CURINGA (`/:referralCode`), então qualquer caminho que
      // não bata com uma rota explícita cai aqui — inclusive o link do
      // afiliado quando o "?" se perde no compartilhamento
      // (`/ref=TADEU05`) e o código solto (`/TADEU05`).
      //
      // Antes, sem academia, a tela renderizava mesmo assim com o texto
      // genérico "Sua academia" e — pior — gravava LESVITA_GYM_REF, fazendo o
      // cadastro nascer vinculado a uma academia que não existe. A indicação
      // do afiliado se perdia e a pessoa via uma tela que não fazia sentido.
      //
      // `error` NÃO entra aqui de propósito: falha de rede na consulta não
      // pode expulsar o visitante de um link de academia legítimo. Só o par
      // "consulta ok + nenhuma linha" prova que este código não é de academia.
      if (!error && !data) {
        const codigo = codigoDeAfiliado(referralCode);
        // Programa Criadores Parceiros: o link do criador é /ref/{CUPOM}. A
        // academia continua tendo precedência (consulta acima); só quando o
        // código NÃO é de academia é que se pergunta se é de um criador.
        if (codigo) {
          const dono = await buscarCriador(codigo);
          if (dono?.VALIDO) {
            setCriador(dono);
            setLoading(false);
            return;
          }
        }
        // `location.replace` e não `navigate`: o useAffiliateTracker roda uma
        // vez na montagem do app, e uma navegação client-side não o
        // dispararia — o código seria capturado por ninguém.
        window.location.replace(codigo ? `/?ref=${codigo}` : "/");
        return;
      }

      setProfile(data);
      setLoading(false);

      const isPartnerUser = themeData?.partner_user === true || data?.partner_user === true;
      setProfile((prev) => (prev ? { ...prev, partner_user: isPartnerUser } : prev));

      localStorage.setItem("LESVITA_GYM_REF", JSON.stringify({
        referralCode,
        capturedAt:         Date.now(),
        origin:             "GYM",
        partnerUser:        isPartnerUser,
        gymName:            data?.gym_name  || null,
        city:               data?.city      || null,
        stateUf:            data?.state_uf  || null,
        logoUrl:            data?.logo_url  || null,
        brandPrimaryColor:  themeData?.brand_primary_color  || null,
        brandAccentColor:   themeData?.brand_accent_color   || null,
        buttonPrimaryBg:    themeData?.button_primary_bg    || null,
        buttonPrimaryText:  themeData?.button_primary_text  || null,
        darkPrimaryColor:   themeData?.dark_primary_color   || null,
        darkAccentColor:    themeData?.dark_accent_color    || null,
        darkNavbarBgColor:  themeData?.dark_navbar_bg_color || null,
      }));

      // Persiste o vínculo de equipe ao lado do gym (mesmo padrão do LESVITA_GYM_REF).
      // Sobrevive a cadastro/login; consumido por VerificacaoCode/Conta/App.jsx.
      if (teamCode || teamId) {
        salvarConvitePendente({
          teamCode, teamId, competitionId, gymCode: referralCode,
        });
      }

      // Registra click (uma vez por dia)
      const clickKey = `LV_REF_CLICK_${referralCode}`;
      const today    = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(clickKey) !== today) {
        localStorage.setItem(clickKey, today);
        if (!error) {
          // O anon perdeu o INSERT direto em referral_events na migration
          // 20260503201044 (RLS) — a escrita crua devolve 401. O trilho oficial
          // é a edge function track-event (service role), que valida o código,
          // grava o evento (source default 'web') e mantém referral_stats.
          try {
            await supabase.functions.invoke("track-event", {
              body: { referral_code: referralCode, event: "click" },
            });
          } catch (_e) { /* registro de click é best-effort — ignora falha */ }
        }
      }
    })();
  }, [referralCode, teamCode]);

  // Minta um join_token para o link de convite que chega sem token (/{code} ou
  // /ref/{code}). É o mesmo trilho do QR dinâmico da TV: o app valida via
  // validate-qr-token, que vincula a academia E concede o premium de parceiro.
  // Sem token, nenhum fluxo nativo consegue vincular. Se a URL já traz join_token
  // (ex.: QR da TV), não minta nada.
  useEffect(() => {
    if (!referralCode || joinToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("mint-referral-token", {
          body: { referral_code: referralCode },
        });
        if (!cancelled && !error && data?.token_id) setMintedToken(data.token_id);
      } catch (_e) {
        // best-effort: sem token o usuário ainda tem o caminho web "já tenho conta".
      }
    })();
    return () => { cancelled = true; };
  }, [referralCode, joinToken]);

  // Garante um token disponível mesmo se o usuário tocar antes do mint de carga
  // terminar (minta sob demanda). Retorna "" se a academia for inválida.
  const resolveToken = async () => {
    if (joinToken)   return joinToken;
    if (mintedToken) return mintedToken;
    try {
      const { data, error } = await supabase.functions.invoke("mint-referral-token", {
        body: { referral_code: referralCode },
      });
      if (!error && data?.token_id) { setMintedToken(data.token_id); return data.token_id; }
    } catch (_e) { /* best-effort */ }
    return "";
  };

  // Query string canônica do link (?join_token=…&team=…), reusada em todos os
  // caminhos (abrir app, clipboard iOS, referrer Android). Aditivo: só inclui o
  // que existir. Prefere o token da URL; senão usa o mintado (override ou estado).
  const buildLinkQuery = (tokenOverride) => {
    const params = new URLSearchParams();
    const token  = tokenOverride || joinToken || mintedToken;
    if (token)    params.set("join_token", token);
    if (teamCode) params.set("team", teamCode);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  // ── Cleanup do timer ao desmontar ────────────────────────────────────────────
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // ── Tenta abrir o app, com fallback para a loja ──────────────────────────────
  const handleOpenApp = async () => {
    setTryingApp(true);

    // Resolve o token antes de montar o link: sem ?join_token o app instalado
    // recebe o deep link mas sai fora sem vincular a academia.
    const token = await resolveToken();

    // Universal Link / App Link — o SO intercepta se o app estiver instalado
    const universalLink = `https://www.lesvita.com/ref/${referralCode}${buildLinkQuery(token)}`;

    // Tenta navegar para o Universal Link
    // Se o app estiver instalado, o SO intercepta e abre o app
    // Se não estiver, o browser fica na página — o timer redireciona para a loja
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // Estratégia: tenta o link, espera 1.5s, se ainda estiver na página → loja
    const storeUrl = IS_IOS ? APP_STORE_URL : PLAY_STORE_URL;

    timerRef.current = setTimeout(() => {
      setTryingApp(false);
      document.body.removeChild(iframe);
      window.location.href = storeUrl;
    }, 1500);

    // Tenta abrir o Universal Link
    iframe.src = universalLink;

    // Se o app abrir, a página perde foco — cancela o redirect
    const onBlur = () => {
      clearTimeout(timerRef.current);
      setTryingApp(false);
      try { document.body.removeChild(iframe); } catch {}
      window.removeEventListener("blur", onBlur);
    };
    window.addEventListener("blur", onBlur);
  };

  // ── Redireciona direto para a loja (sem tentar abrir) ────────────────────────
  // No iOS, antes de redirecionar, grava o link no clipboard — deferred deep link:
  // o app recém-instalado lê esse valor (via ClipboardReferralPlugin) e aplica
  // o vínculo automaticamente, sem o usuário precisar reler o QR.
  const handleDownload = async () => {
    if (IS_IOS) {
      try {
        // Carrega gym + team (buildLinkQuery) no clipboard — deferred deep link.
        const canonicalUrl = `https://www.lesvita.com/ref/${referralCode}${buildLinkQuery()}`;
        await navigator.clipboard.writeText(canonicalUrl);
      } catch {
        // Sem gesto do usuário, permissão negada ou sem HTTPS — não bloqueia o redirect.
      }
      window.location.href = APP_STORE_URL;
      return;
    }

    // Android: passa join_token + team via parâmetro referrer para a Play Install
    // Referrer API. O app recém-instalado lê esse valor em InstallReferrerPlugin.
    const effectiveToken = joinToken || mintedToken;
    if (effectiveToken || teamCode) {
      const parts = [];
      if (effectiveToken) parts.push(`join_token=${effectiveToken}`);
      parts.push(`gym=${referralCode}`);
      if (teamCode) parts.push(`team=${teamCode}`);
      const encoded = encodeURIComponent(parts.join("&"));
      window.location.href = `${PLAY_STORE_URL}&referrer=${encoded}`;
    } else {
      window.location.href = PLAY_STORE_URL;
    }
  };

  // ── Fluxo de login web (usuário já tem o app/conta) ──────────────────────────
  const handleGoLogin = async () => {
    try {
      const currentUser = auth.currentUser;

      const gymParams = new URLSearchParams({
        tab: "cadastro", gymRef: referralCode,
        gymName: profile?.gym_name || "", gymCity: profile?.city || "",
        gymState: profile?.state_uf || "", gymLogo: profile?.logo_url || "",
        gymPartner: profile?.partner_user ? "1" : "0", gymAt: String(Date.now()),
        gymTeam: teamCode || "",
      }).toString();

      if (!currentUser) { navigate(`/login?${gymParams}`); return; }

      const referralRaw = localStorage.getItem("LESVITA_GYM_REF");
      const referral    = referralRaw ? JSON.parse(referralRaw) : null;
      if (!referral?.referralCode) { navigate("/plataforma/pessoal"); return; }

      const statusRes  = await getGymReferralStatus({ referralCode: referral.referralCode });
      const statusData = statusRes?.data || {};

      setNewGym({ code: referral.referralCode, gymName: referral.gymName, city: referral.city, stateUf: referral.stateUf, partnerUser: referral.partnerUser });

      if (!statusData?.hasUser) { navigate("/plataforma/pessoal"); return; }

      if (!statusData?.currentGymReferral?.APPLIED) {
        setGymReferralAction("link"); setShowGymReferralModal(true); return;
      }

      if (statusData?.currentGymReferral?.CODE && statusData.currentGymReferral.CODE !== referral.referralCode) {
        setCurrentGym({ code: statusData.currentGymReferral.CODE, gymName: statusData.currentGymReferral.GYM_NAME, city: statusData.currentGymReferral.CITY, stateUf: statusData.currentGymReferral.STATE_UF });
        setGymReferralAction("switch"); setShowGymReferralModal(true); return;
      }

      // Já vinculado à MESMA academia: o vínculo de gym não muda (nenhum modal),
      // mas ainda pode haver um link de equipe pendente (?team=CODE). Sem isto, a
      // equipe nunca era aplicada neste caminho. applyGymReferralToUser é
      // idempotente p/ o gym (action "unchanged") e roda a inscrição na equipe.
      // A URL ATUAL vence sobre o convite guardado — a precedência invertida
      // aqui era o que fazia um convite abandonado sobrescrever o link recém
      // aberto. O guardado ainda serve de fallback, mas só se for recente e da
      // MESMA academia (ver src/utils/teamJoinStorage.js).
      const convite = resolverConvite(
          {teamCode, teamId, competitionId}, referral.referralCode);
      const pendingTeam = convite?.teamCode || null;
      if (convite) {
        try {
          await applyGymReferralToUser({
            referralCode: referral.referralCode,
            gymName: referral.gymName || null, city: referral.city || null,
            stateUf: referral.stateUf || null, origin: referral.origin || "GYM",
            partnerUser: referral.partnerUser === true,
            capturedAt: referral.capturedAt || Date.now(),
            logoUrl: referral.logoUrl || null, teamCode: pendingTeam,
            // Formato NOVO: quando o link traz o endereço exato, ele vai junto
            // e o backend nem precisa resolver JOIN_CODE.
            competitionId: convite.competitionId || null,
            teamId: convite.teamId || null,
          });
        } catch (teamErr) {
          console.error("[ReferralLanding] falha ao aplicar equipe (já vinculado):", teamErr);
        }
      }

      localStorage.removeItem("LESVITA_GYM_REF");
      localStorage.removeItem("LESVITA_TEAM_JOIN");
      navigate("/plataforma/pessoal");
    } catch (error) {
      console.error("[ReferralLanding] erro:", error);
      navigate("/login");
    }
  };

  const handleConfirmGymReferral = async () => {
    try {
      setIsApplyingGymReferral(true);
      const referral = JSON.parse(localStorage.getItem("LESVITA_GYM_REF") || "{}");
      if (!referral?.referralCode) { setShowGymReferralModal(false); navigate("/plataforma/pessoal"); return; }
      // Mesma correção de precedência do outro caminho: a URL atual vence.
      const convite = resolverConvite(
          {teamCode, teamId, competitionId}, referral.referralCode);
      await applyGymReferralToUser({ referralCode: referral.referralCode, gymName: referral.gymName || null, city: referral.city || null, stateUf: referral.stateUf || null, origin: referral.origin || "GYM", partnerUser: referral.partnerUser === true, capturedAt: referral.capturedAt || Date.now(), logoUrl: referral.logoUrl || null, teamCode: convite?.teamCode || null, competitionId: convite?.competitionId || null, teamId: convite?.teamId || null });
      localStorage.removeItem("LESVITA_GYM_REF");
      limparConvitePendente();
      setShowGymReferralModal(false);
      navigate("/plataforma/pessoal");
    } catch (error) {
      console.error("[ReferralLanding] erro ao aplicar vínculo:", error);
    } finally {
      setIsApplyingGymReferral(false);
    }
  };

  const handleCancelGymReferral = () => {
    localStorage.removeItem("LESVITA_GYM_REF"); localStorage.removeItem("LESVITA_TEAM_JOIN"); setShowGymReferralModal(false); navigate("/plataforma/pessoal");
  };

  const copyAboveButton = useMemo(() => {
    const options = [
      "Você tá a 1 passo da sua avaliação inteligente.",
      "Bora destravar seus resultados com a LesVita.",
      "Sua jornada começa agora — entra pra ver seu progresso.",
      "Acesso liberado: avaliação, metas e evolução no seu bolso.",
      "Chegou a hora de acompanhar seu corpo de verdade.",
    ];
    return options[Math.floor(Math.random() * options.length)];
  }, []);

  if (criador) {
    return <CriadorLink criador={criador} origem={searchParams.get("origem") || "link"} />;
  }

  const gymLogo = profile?.logo_url || null;
  const gymName = profile?.gym_name || "Sua academia";

  return (
    <div className={Style.wrapper}>
      <div className={Style.container}>
        <div className={Style.header}>
          <h1 className={Style.title}>{loading ? "Carregando..." : "Bem-vindo(a)!"}</h1>
          <p className={Style.subtitle}>{loading ? "Só um segundo…" : `Você acessou pela ${gymName}.`}</p>
        </div>

        <BoxShadow className={Style.card}>
          {/* Logos */}
          <div className={Style.brandRow}>
            <div className={Style.logoBox}>
              {gymLogo
                ? <img src={gymLogo} alt="Logo da academia" className={Style.logoImg} />
                : <span className={Style.logoFallback}>🏋️</span>
              }
            </div>
            <div className={Style.plus}>+</div>
            <div className={Style.logoBox}>
              <img src={LESVITA_LOGO} alt="Logo LesVita" className={Style.logoImg} />
            </div>
          </div>

          <div className={Style.content}>
            <p className={Style.copy}>{copyAboveButton}</p>

            {/* Botões para mobile (sem app instalado) */}
            {IS_MOBILE && (
              <div className={Style.mobileActions}>

                {/* Botão principal (destaque): abre o app se já instalado e vincula a academia */}
                <button
                  type="button"
                  className={`${Style.button} ${Style.buttonPrimary}`}
                  onClick={handleOpenApp}
                  disabled={tryingApp}
                >
                  {tryingApp ? "Abrindo o app..." : "Abrir no app LesVita"}
                </button>

                <p className={Style.mobileHint}>
                  Já tem o app? Toque acima para entrar direto e vincular sua academia.
                </p>

                {/* Botão secundário: baixar na loja (para quem ainda não tem o app) */}
                <button
                  type="button"
                  onClick={handleDownload}
                  className={Style.storeButton}
                >
                  {IS_IOS ? (
                    <>
                      <img src={APP_STORE_LOGO} height={24} alt="App Store" />
                      Ainda não tenho — baixar na App Store
                    </>
                  ) : (
                    <>
                      <img src={PLAY_STORE_LOGO}  height={24} alt="Google Play" />
                      Ainda não tenho — baixar no Google Play
                    </>
                  )}
                </button>


                {/* Link de login web (para quem já tem conta) */}
                <button
                  type="button"
                  onClick={handleGoLogin}
                  className={Style.textButton}
                >
                  Já tenho conta — entrar pelo navegador
                </button>
              </div>
            )}

            {/* Desktop ou sem detecção de mobile */}
            {!IS_MOBILE && (
              <>
                <p className={Style.desktopHint}>
                  Escaneie o QR abaixo com seu celular para baixar o app LesVita:
                </p>
                <div className={Style.storeRow}>
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                    className={`${Style.storeLink} ${Style.storeLinkApple}`}>
                    🍎 App Store
                  </a>
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                    className={`${Style.storeLink} ${Style.storeLinkPlay}`}>
                    ▶ Google Play
                  </a>
                </div>
                <button type="button" onClick={handleGoLogin} className={`${Style.button} ${Style.desktopLogin}`}>
                  Entrar pelo navegador
                </button>
              </>
            )}

            <p className={Style.hint}>
              Ao continuar, seu acesso fica vinculado a esta academia.
            </p>
          </div>
        </BoxShadow>

        <div className={Style.footer}>
          Código: <span className={Style.mono}>{referralCode}</span>
        </div>
      </div>

      <Modal show={showGymReferralModal} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>{gymReferralAction === "switch" ? "Trocar vínculo de academia" : "Vincular academia"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gymReferralAction === "switch" ? (
            <>
              <p>Você já possui vínculo com <strong>{currentGym?.gymName || "sua academia atual"}</strong>. Deseja trocar para <strong>{newGym?.gymName || "a nova academia"}</strong>?</p>
              <div className={Style.switchLine}>{currentGym?.gymName} → {newGym?.gymName}</div>
            </>
          ) : (
            <p>Deseja vincular sua conta com <strong>{newGym?.gymName || "esta academia"}</strong>?</p>
          )}
        </Modal.Body>
        <Modal.Footer className={Style.footerModal}>
          <button type="button" onClick={handleCancelGymReferral} disabled={isApplyingGymReferral}>Agora não</button>
          <button type="button" onClick={handleConfirmGymReferral} disabled={isApplyingGymReferral}>
            {isApplyingGymReferral ? "Confirmando..." : "Confirmar"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}